import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { format } from 'date-fns'
import { nl } from 'date-fns/locale'
import {
  useDossiers,
  useDossier,
  useInventoryComponents,
  useInventoryVulnerabilities,
  useAllInventoryVulnerabilities,
  useRefreshInventoryIntel,
  useGenerateSbomToken,
} from '@/hooks/useApi'
import type { InventoryComponent } from '@/api/client'
import { generateVulnerabilityPrompt } from '@/lib/vulnerabilityPrompt'
import { InstructionsPanel } from '@/components/InstructionsPanel'
import { CveSeverityBadge } from '@/components/CveSeverityBadge'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Select } from '@/components/ui/select'
import { Spinner } from '@/components/ui/spinner'
import { cn } from '@/lib/utils'
import { RefreshCw, ExternalLink, ShieldAlert, ArrowLeft, Bot, AlertTriangle, KeyRound, Copy } from 'lucide-react'

function ComponentRow({
  component,
  selected,
  onSelect,
}: {
  component: InventoryComponent
  selected: boolean
  onSelect: () => void
}) {
  return (
    <button
      onClick={onSelect}
      className={cn(
        'w-full text-left px-3 py-2.5 rounded-md border flex items-center justify-between gap-3 transition-colors',
        selected ? 'border-primary bg-primary/5' : 'border-transparent hover:bg-muted/50',
      )}
    >
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-medium text-sm truncate">{component.name}</span>
          <span className="text-xs text-muted-foreground">{component.version}</span>
          {component.is_outdated && (
            <Badge variant="outline" className="text-xs">
              → {component.latest_version ?? '?'}
            </Badge>
          )}
        </div>
        <div className="text-xs text-muted-foreground mt-0.5">
          {component.ecosystem} · {component.component_type}
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {component.kev_count > 0 && (
          <Badge className="bg-red-700 text-white" title="Actief misbruikt (CISA KEV)">
            ⚡ {component.kev_count}
          </Badge>
        )}
        {component.vuln_count > 0 ? (
          <Badge className="bg-red-500 text-white">{component.vuln_count} CVE{component.vuln_count === 1 ? '' : "'s"}</Badge>
        ) : (
          <Badge variant="outline" className="text-green-700 dark:text-green-300 border-green-300 dark:border-green-800">geen CVE's</Badge>
        )}
      </div>
    </button>
  )
}

/**
 * The product's link to its own deploy pipeline. The token is shown exactly
 * once (the backend only ever exposes has_sbom_token afterwards), and rotating
 * invalidates the old one on the spot — hence the confirm.
 */
function SbomLinkCard({ dossierId, hasToken }: { dossierId: number; hasToken: boolean }) {
  const generate = useGenerateSbomToken()
  const [token, setToken] = useState<string | null>(null)

  const create = () => {
    if (hasToken && !window.confirm('Het huidige token stopt meteen met werken. De CI-secret moet je dan ook vervangen. Doorgaan?')) return
    generate.mutate(dossierId, {
      onSuccess: ({ sbom_token }) => setToken(sbom_token),
      onError: () => toast.error('Token aanmaken mislukt'),
    })
  }

  return (
    <Card>
      <CardContent className="py-4 space-y-3">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2 text-sm">
            <KeyRound className="h-4 w-4 text-muted-foreground" />
            <span className="font-medium">SBOM-koppeling</span>
            <span className="text-muted-foreground">
              {hasToken
                ? '— eigen token ingesteld'
                : '— geen eigen token (het gedeelde token met dossiernaam werkt ook)'}
            </span>
          </div>
          <Button size="sm" variant="outline" onClick={create} disabled={generate.isPending}>
            {generate.isPending ? <Spinner className="h-4 w-4" /> : hasToken ? 'Token roteren' : 'Token aanmaken'}
          </Button>
        </div>
        {token && (
          <div className="rounded-md border border-amber-500/50 bg-amber-500/5 p-3 space-y-2">
            <p className="text-sm">
              Kopieer dit token nu, het wordt niet opnieuw getoond. Zet het als secret{' '}
              <code className="text-xs">SBOM_INGEST_TOKEN</code> in de CI van dit product.
            </p>
            <div className="flex items-center gap-2">
              <code className="flex-1 truncate rounded bg-muted px-2 py-1 text-xs">{token}</code>
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  navigator.clipboard.writeText(token).then(
                    () => toast.success('Token gekopieerd'),
                    () => toast.error('Kopiëren mislukt — selecteer het token met de hand'),
                  )
                }}
              >
                <Copy className="h-4 w-4 mr-1" /> Kopieer
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

/**
 * A CVE list is only as current as the SBOM it was matched against. Without
 * this, an inventory that stopped being pushed weeks ago looks exactly like
 * one that is up to date, and "0 open CVEs" reads as good news rather than as
 * no news.
 */
const SBOM_STALE_DAYS = 7

function isStale(lastInventoryAt: string): boolean {
  const age = Date.now() - new Date(lastInventoryAt).getTime()
  return age > SBOM_STALE_DAYS * 24 * 60 * 60 * 1000
}

export default function Inventory() {
  const { targetId: dossierIdParam } = useParams<{ targetId: string }>()
  const navigate = useNavigate()
  const { data: targets } = useDossiers({ active: true })
  const targetId = Number(dossierIdParam) || targets?.[0]?.id || 0

  const { data: target } = useDossier(targetId)
  const { data: components, isLoading: componentsLoading } = useInventoryComponents(targetId)
  const [selectedComponentId, setSelectedComponentId] = useState<number | null>(null)
  const { data: vulnerabilities, isLoading: vulnsLoading } = useInventoryVulnerabilities(selectedComponentId)
  const refreshIntel = useRefreshInventoryIntel()

  const [showPrompt, setShowPrompt] = useState(false)
  const { data: allVulnerabilities, isLoading: allVulnsLoading } = useAllInventoryVulnerabilities(
    showPrompt ? targetId : 0
  )

  const sortedComponents = [...(components ?? [])].sort((a, b) => {
    if (a.kev_count !== b.kev_count) return b.kev_count - a.kev_count
    if (a.vuln_count !== b.vuln_count) return b.vuln_count - a.vuln_count
    return a.name.localeCompare(b.name)
  })
  const selectedComponent = sortedComponents.find((c) => c.id === selectedComponentId)
  const totalVulns = (components ?? []).reduce((sum, c) => sum + c.vuln_count, 0)
  const totalKev = (components ?? []).reduce((sum, c) => sum + c.kev_count, 0)

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <Button variant="ghost" size="sm" onClick={() => navigate('/inventory')} className="mb-1 -ml-2">
            <ArrowLeft className="h-4 w-4 mr-1" /> Inventaris
          </Button>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <ShieldAlert className="h-6 w-6" />
            Dependencies & kwetsbaarheden{target ? ` — ${target.name}` : ''}
          </h1>
          {target?.last_inventory_at ? (
            <p className="text-sm text-muted-foreground">
              Laatste SBOM: {format(new Date(target.last_inventory_at), 'd MMM yyyy HH:mm', { locale: nl })}
              {isStale(target.last_inventory_at) && (
                <span className="ml-2 inline-flex items-center gap-1 text-amber-600 dark:text-amber-400">
                  <AlertTriangle className="h-3.5 w-3.5" />
                  ouder dan {SBOM_STALE_DAYS} dagen — de CI/CD-push draait niet
                </span>
              )}
            </p>
          ) : (
            <p className="flex items-center gap-1 text-sm text-amber-600 dark:text-amber-400">
              <AlertTriangle className="h-3.5 w-3.5" />
              Nog geen SBOM ontvangen — de componenten hieronder kunnen verouderd zijn
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          {targets && targets.length > 1 && (
            <Select
              value={String(targetId)}
              onChange={(e) => navigate(`/inventory/${e.target.value}`)}
              className="w-56 text-sm"
            >
              {targets.map((t) => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </Select>
          )}
          <Button
            size="sm"
            variant="outline"
            disabled={refreshIntel.isPending || !targetId}
            onClick={() => {
              refreshIntel.mutate(targetId, {
                onSuccess: ({ result }) => {
                  const msg = result.new_cves > 0
                    ? `CVE-check klaar: ${result.new_cves} nieuwe kwetsbaarheid${result.new_cves === 1 ? '' : 'en'} gevonden (${result.osv_components} componenten gecontroleerd)`
                    : `CVE-check klaar: geen nieuwe kwetsbaarheden (${result.osv_components} componenten gecontroleerd)`
                  toast.success(msg)
                },
                onError: () => toast.error('CVE-check mislukt'),
              })
            }}
          >
            {refreshIntel.isPending ? <Spinner className="h-4 w-4" /> : <RefreshCw className="h-4 w-4 mr-1" />}
            Ververs CVE-data
          </Button>
          <Button
            size="sm"
            disabled={!totalVulns && !(components ?? []).some((c) => c.is_outdated)}
            onClick={() => setShowPrompt(true)}
          >
            <Bot className="h-4 w-4 mr-1" />
            Claude Code instructies
          </Button>
        </div>
      </div>

      {showPrompt && target && (
        <InstructionsPanel
          title={`Claude Code instructies — ${target.name}`}
          prompt={
            allVulnerabilities
              ? generateVulnerabilityPrompt(target, components ?? [], allVulnerabilities)
              : ''
          }
          loading={allVulnsLoading}
          onClose={() => setShowPrompt(false)}
        />
      )}

      {target && <SbomLinkCard dossierId={target.id} hasToken={target.has_sbom_token} />}

      {!targetId ? (
        <Card>
          <CardContent className="py-8 text-center text-sm text-muted-foreground">
            Nog geen platform geregistreerd. Voeg er een toe op de Platformen-pagina.
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle>Components ({(components ?? []).length})</CardTitle>
              <CardDescription>
                {totalKev > 0 && <span className="text-red-700 dark:text-red-300 font-medium">{totalKev} actief misbruikt (KEV) · </span>}
                {totalVulns} kwetsbaarhe{totalVulns === 1 ? 'id' : 'den'} in totaal
              </CardDescription>
            </CardHeader>
            <CardContent>
              {componentsLoading ? (
                <Spinner />
              ) : !sortedComponents.length ? (
                <p className="text-sm text-muted-foreground">
                  Nog geen componenten binnengehaald. Wacht op de eerste SBOM-ingest
                  (via CI, of de "Ververs CVE-data"-knop hierboven na een handmatige ingest).
                </p>
              ) : (
                <div className="space-y-1 max-h-[600px] overflow-y-auto">
                  {sortedComponents.map((c) => (
                    <ComponentRow
                      key={c.id}
                      component={c}
                      selected={c.id === selectedComponentId}
                      onSelect={() => setSelectedComponentId(c.id === selectedComponentId ? null : c.id)}
                    />
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>
                {selectedComponent ? `${selectedComponent.name}@${selectedComponent.version}` : 'Kwetsbaarheden'}
              </CardTitle>
              <CardDescription>
                {selectedComponent ? 'CVE-details voor dit component' : 'Kies een component links'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {!selectedComponentId ? (
                <p className="text-sm text-muted-foreground">
                  Klik op een component om de CVE-details te zien.
                </p>
              ) : vulnsLoading ? (
                <Spinner />
              ) : !vulnerabilities?.length ? (
                <p className="text-sm text-green-700 dark:text-green-300">Geen bekende kwetsbaarheden voor dit component.</p>
              ) : (
                <div className="space-y-3 max-h-[600px] overflow-y-auto">
                  {vulnerabilities.map((v) => (
                    <div key={v.id} className="border rounded-md p-3 space-y-1.5">
                      <div className="flex items-center gap-2 flex-wrap">
                        <code className="text-sm font-semibold">{v.vuln_id}</code>
                        <CveSeverityBadge severity={v.severity} />
                        {v.kev && <Badge className="bg-red-700 text-white">⚡ CISA KEV</Badge>}
                        {v.suppressed && <Badge variant="outline">onderdrukt</Badge>}
                      </div>
                      <div className="text-xs text-muted-foreground flex items-center gap-3 flex-wrap">
                        {v.cvss_score !== null && <span>CVSS {v.cvss_score.toFixed(1)}</span>}
                        {v.epss_score !== null && <span>EPSS {(v.epss_score * 100).toFixed(2)}%</span>}
                        {v.fixed_version && <span>Fix: <code>{v.fixed_version}</code></span>}
                      </div>
                      {v.description && (
                        <p className="text-xs text-muted-foreground line-clamp-3">{v.description}</p>
                      )}
                      {v.advisory_url && (
                        <a
                          href={v.advisory_url}
                          target="_blank"
                          rel="noreferrer"
                          className="text-xs text-blue-700 dark:text-blue-300 inline-flex items-center gap-1 hover:underline"
                        >
                          Advisory <ExternalLink className="h-3 w-3" />
                        </a>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  )
}
